export const identity = (x) => x;

export const cardinal = (fn) => (a) => (b) => fn(b)(a);

export const vireo = (a) => (b) => (fn) => fn(a)(b);

export const thrush = (a) => (fn) => fn(a);

export const leftApply = (fn, ...a) => (...b) => fn(...a, ...b);

export const rightApply = (fn, ...b) => (...a) => fn(...a, ...b);

export const tap = (value) => (fn) => {
	return typeof fn === "function" && fn(value), value;
};

export const compose = (...fns) => (arg) =>
	fns.reduceRight((x, fn) => fn(x), arg);

export const partial = (fn, ...args) => fn.bind(null, ...args);

export const is = (type) => (object) => Object(object) instanceof type;

export const type = (type) => (object) => typeof object === type;

export const unfold = (fn, seed) => {
	function* go(fn, seed) {
		let result = fn(seed);

		while (result) {
			yield result[0];
			result = fn(result[1]);
		}
	}

	return [...go(fn, seed)];
};

export const memoize = (fn, serialize = JSON.stringify) => {
	const cache = {};

	return (...args) => {
		const key = serialize(args);
		return cache.hasOwnProperty(key) ? cache[key] : (cache[key] = fn(...args));
	};
};

export const memoizeAsync = (fn, serialize = JSON.stringify) => {
	const memo = memoize(fn, serialize);
	return async (...args) => await memo(...args);
};

export const fetchResource = memoizeAsync(async (resource) => {
	return await fetch(`api/${resource}.php`).then((r) => r.json());
});

export const wait = (timeout, callback, ...args) => {
	try {
		callback(...args);
	} catch (error) {
		const next_timeout = Math.round(timeout * 1.5);
		setTimeout(wait, timeout, next_timeout, callback, ...args);
	}
};

export const freeze = (object) => {
	const freezable = (object) => {
		const fns = ["array", "string"].map(type);
		return !Object.isFrozen(object) && fns.every((fn) => !fn(object));
	};

	Object.values(object).forEach((value) => {
		if (value && freezable(value)) {
			freeze(value);
		}
	});

	return Object.freeze(object);
};

/* String helper functions */

export const split = (delimiter) => (string) => string.split(delimiter);

/**
 * Converts hyphenated text to pascal case.
 * @param {String} str The hyphenated text we wish to convert to pascal case.
 */
export const formatToPascalCase = (str) =>
	str.replace(/(-[a-zA-Z])/g, (m) => m.replace("-", "").toUpperCase());

/**
 * Converts pascal case text to hyphenated text.
 * @param {String} str The pascal case text we wish to hyphenate.
 */
export const formatToHyphenated = (str) =>
	str.replace(/[A-Z]/g, (m, i) => (i > 0 ? "-" : "") + m.toLowerCase());

export const replace = (search, replacement) => (string) =>
	string.replace(search, replacement);

/**
 * Capitalizes the first character of a given string.
 * @param {String} str The string to operate on.
 */
export const upperCaseFirst = (str) => str[0].toUpperCase() + str.slice(1);

/**
 * Returns a capitalized version of the given string.
 * @param {String} text The string to capitalize.
 * @param {String|RegExp} delimiter The word delimiter.
 */
export const capitalize = (text, delimiter = " ") =>
	compose(join(delimiter), map(upperCaseFirst), split(delimiter))(text);

export const lowercase = (str) => str.toLowerCase();

/* Array helper functions */

export const map = (fn) => (array) => array.map(fn);

export const filter = (fn) => (array) => array.filter(fn);

export const reduce = (fn, init = null) => (array) => array.reduce(fn, init);

export const join = (separator) => (array) => array.join(separator);

export const column = (idx) => (array) => compose(map, prop)(idx)(array);

export const append = (value) => (array) => [...array, value];

export const prepend = (value) => (array) => [value, ...array];

export const distinct = (function () {
	const set = compose((xs) => new Set(xs), map(JSON.stringify));
	const unique = compose(map(JSON.parse), Array.from, set);

	return (array, property) => {
		if (property === undefined || property === null) {
			return unique(array);
		} else {
			const callback = type("function")(property) ? property : prop(property);

			return Object.values(
				array.reduce((distinct, element, index, self) => {
					const value = callback(element, index, self);

					if (!distinct.hasOwnProperty(value)) {
						distinct[value] = element;
					}

					return distinct;
				}, {})
			);
		}
	};
})();

export const group = (array, property) => {
	const callback = type("function")(property) ? property : prop(property);

	return array.reduce((group, element, index, array) => {
		const name = callback(element, index, array);
		const exists = group.hasOwnProperty(name);

		group[name] = exists ? [...group[name], element] : [element];

		return group;
	}, {});
};

/**
 * Creates a new HTML element.
 *
 * @param {String} tagName The tag name for the element we wish to create.
 * @param {{}} attributes The attributes to give the element.
 */
export const createElement = (tagName, attributes, children = []) => {
	const element = document.createElement(tagName);

	Object.entries(attributes).forEach(([key, value]) => {
		if (key === "dataset") {
			Object.entries(value).forEach(([k, v]) => {
				element.dataset[formatToPascalCase(k)] = v;
			});
		} else if (key.includes("data-")) {
			const prop = formatToPascalCase(key.replace("data-", ""));
			element.dataset[prop] = value;
		} else {
			element[key] = value;
		}
	});

	element.append(...children);
	return element;
};

export const updateIndicators = (index, targets) => {
	return targets.map((_, i) => {
		const cls =
			i === index ? "bubble--active" : i < index ? "bubble--seen" : "";
		return createElement("div", { className: `bubble ${cls}`.trim() });
	});
};

export const decodeHTMLEntities = (text) => {
	return createElement("textarea", { innerHTML: text }).value;
};

export const encodeHTMLEntities = (text) => {
	return createElement("div", { innerText: text }).innerHTML;
};

export const isIOS = () => {
	return (
		[
			"iPad Simulator",
			"iPhone Simulator",
			"iPod Simulator",
			"iPad",
			"iPhone",
			"iPod",
		].includes(navigator.platform) ||
		(navigator.userAgent.includes("Mac") && "ontouchend" in document)
	);
};

export const isTouchDevice = () => {
	return "ontouchstart" in document.documentElement;
};

export const getElementOffset = (() => {
	const getRect = (element) => {
		const rect = element.getBoundingClientRect();

		return {
			top: rect.top + window.pageYOffset,
			left: rect.left + window.pageXOffset,
		};
	};

	const getOffset = (element) => {
		let top = 0;
		let left = 0;

		do {
			top += element.offsetTop || 0;
			left += element.offsetLeft || 0;
			element = element.offsetParent;
		} while (element);

		return { top, left };
	};

	return (element, useBoundingClientRect = true) => {
		const get = useBoundingClientRect ? getRect : getOffset;
		return get(element);
	};
})();

export const dimensions = (element, padding = { v: 0, h: 0 }) => {
	const { top, left, width, height } = element.getBoundingClientRect();
	const vh = padding && padding.v ? padding.v / 2 : 0;
	const hh = padding && padding.h ? padding.h / 2 : 0;
	const half = padding ? padding / 2 : 0;

	const dimensions = {
		top: Math.floor(top - vh),
		left: Math.floor(left - hh),
		width: Math.floor(width + (padding.h ?? 0)),
		height: Math.floor(height + (padding.v ?? 0)),
	};

	return dimensions;
};

export const lerp = (a, b, t) => {
	return Math.floor((1.0 - t) * a + b * t);
};

export const parse_text = (content, element, data) => {
	const attr = /\[textContent\]/gi;
	const dRegex = /\[data ([a-zA-Z]+)\]/i;
	let text = content;

	if (attr.test(text) && element) {
		text = text.replace(attr, element.textContent).trim();
	}

	if (dRegex.test(text) && data) {
		const res = Array.from(text.match(dRegex));

		if (res.length) {
			text = text.replace(dRegex, data[res[1]]).trim();
		}
	}

	return text;
};

export const parse = (content, target, data) => {
	if (content === undefined) {
		return "";
	}

	const parsed = parse_text(content, target, data);

	return parsed.split("{:p}").reduce((html, content) => {
		content = content.replace(/\{:u\}(\w+)/g, (_, $1) => `<u>${$1}</u>`);
		return `${html}<p>${content}</p>`;
	}, "");
};
